import React, {useState, useEffect, useMemo} from 'react';
import {voyageService, carService, printService} from '../services';
import {Voyage, Car, Shipper} from '../types';
import {Layout} from '../components/layout';
import {SearchableSelect} from '../components';
import {formatDateOrToday} from '../utils/tableUtils';
import InventoryList from './InventoryList';

const ParkingListPage: React.FC = () => {
    const [voyages, setVoyages] = useState<Voyage[]>([]);
    const [selectedVoyage, setSelectedVoyage] = useState<Voyage | null>(null);
    const [selectedShipperId, setSelectedShipperId] = useState<string>('');
    const [cars, setCars] = useState<Car[]>([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [includeRelated, setIncludeRelated] = useState(true);
    const [sortBy, setSortBy] = useState<string>('');

    // Get unique shippers from cars
    const shippers = useMemo(() => {
        const shipperMap = new Map<string, Shipper>();
        cars.forEach(car => {
            if (car.shipper && car.shipper.id) {
                shipperMap.set(car.shipper.id, car.shipper);
            }
        });
        return Array.from(shipperMap.values()).sort((a, b) => a.code.localeCompare(b.code));
    }, [cars]);

    // Filter cars by selected shipper
    const filteredCars = useMemo(() => {
        if (!selectedShipperId) return cars;
        return cars.filter(car => car.shipper?.id === selectedShipperId);
    }, [cars, selectedShipperId]);

    const selectedShipper = useMemo(() => {
        return shippers.find(s => s.id === selectedShipperId) || null;
    }, [shippers, selectedShipperId]);

    useEffect(() => {
        const loadVoyages = async () => {
            try {
                const data = await voyageService.getAll({limit: 1000, is_archived: false});
                setVoyages(data as Voyage[]);
            } catch (error) {
                console.error('Error loading voyages:', error);
            }
        };
        loadVoyages();
    }, []);

    const sortedVoyages = useMemo(() => {
        return [...voyages].sort((a, b) => a.voyage_code.localeCompare(b.voyage_code));
    }, [voyages]);

    useEffect(() => {
        if (!selectedVoyage) {
            setCars([]);
            setSelectedShipperId('');
            return;
        }

        const loadCars = async () => {
            setLoading(true);
            try {
                const response = await carService.getByVoyage(selectedVoyage.id, {limit: 10000});
                setCars(response.data as Car[]);
                setSelectedShipperId('');
            } catch (error) {
                console.error('Error loading cars:', error);
            } finally {
                setLoading(false);
            }
        };
        loadCars();
    }, [selectedVoyage]);


    const generatePDF = async () => {
        if (!selectedVoyage) return;

        setGenerating(true);

        const shipperPart = selectedShipper
            ? selectedShipper.name
            : 'All Shippers';
        const filename = `${shipperPart} ${selectedVoyage.voyage_code} ${selectedVoyage.vessel_description} Parking List.pdf`;

        try {
            await printService.generateInventoryList({
                voyageNo: selectedVoyage.voyage_code,
                date: formatDateOrToday(selectedVoyage.departure),
                shipName: selectedVoyage.vessel_description,
                loadingPort: selectedVoyage.port_name || '',
                shipperName: selectedShipper ? `${selectedShipper.code} - ${selectedShipper.name}` : undefined,
                cars: inventoryCars,
                title: 'PARKING LIST',
                showCollect: false,
                includeRelated
            }, filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setGenerating(false);
        }
    };

    const inventoryCars = useMemo(() => {
        const mapped = filteredCars.map(car => {
            const dbMake = car.make?.name || '';
            const dbModel = car.model?.name || '';
            // Use database make/model if available, otherwise use manual_make_model
            const make = dbMake || car.manual_make_model || '';
            const model = dbMake ? dbModel : ''; // Only use model if we have database make

            // Build related cars array (sorted: Attached first, then STC)
            const relatedCars = [...(car.related_cars || [])]
                .sort((a, b) => {
                    if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
                    if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
                    return 0;
                })
                .map(rc => {
                    const rcDbMake = rc.make?.name || '';
                    const rcDbModel = rc.model?.name || '';
                    // Use database make/model if available, otherwise use manual_make_model
                    const rcMake = rcDbMake || rc.manual_make_model || '';
                    const rcModel = rcDbMake ? rcDbModel : '';
                    return {
                        chassisNo: rc.chassis_number,
                        make: rcMake,
                        model: rcModel,
                        manualMakeModel: rc.manual_make_model || undefined,
                        relationshipType: rc.relationship_type || 'STC'
                    };
                });

            return {
                refNo: car.reference_number,
                dealer: car.shipper?.code || '',
                port: car.port?.port || '',
                orderNo: car.booking_reference || '',
                consignee: car.consignee?.name || car.name || '',
                chassisNo: car.chassis_number,
                make,
                model,
                manualMakeModel: car.manual_make_model || undefined,
                type: car.category ?? car.model?.type ?? '',
                relatedCars: relatedCars.length > 0 ? relatedCars : undefined
            };
        });

        if (sortBy) {
            mapped.sort((a, b) => {
                if (sortBy === 'make/model') {
                    const makeCompare = (a.make || '').localeCompare(b.make || '');
                    if (makeCompare !== 0) return makeCompare;
                    return (a.model || '').localeCompare(b.model || '');
                }
                const valA = (a as any)[sortBy];
                const valB = (b as any)[sortBy];
                // Handle numeric vs string comparison
                if (typeof valA === 'number' && typeof valB === 'number') {
                    return valA - valB;
                }
                return String(valA || '').localeCompare(String(valB || ''));
            });
        }

        return mapped.map((car, index) => ({...car, position: index + 1}));
    }, [filteredCars, sortBy]);

    return (
        <>
            {/* Screen view with layout */}
            <div className="no-print">
                <Layout>
                    <div className="space-y-6">
                        {/* Header + Selection */}
                        <div
                            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                Parking List
                            </h1>
                            <p className="text-gray-500 dark:text-gray-400 mb-4">
                                Select a voyage to generate the parking list for printing
                            </p>
                            <div className="flex gap-3 items-center flex-wrap">
                                <div className="w-52">
                                    <SearchableSelect
                                        options={sortedVoyages.map(voyage => ({
                                            value: voyage.id,
                                            label: `${voyage.voyage_code} - ${voyage.vessel_description}`
                                        }))}
                                        value={selectedVoyage?.id || ''}
                                        onChange={(value) => {
                                            const voyage = sortedVoyages.find(v => v.id === value);
                                            setSelectedVoyage(voyage || null);
                                        }}
                                        placeholder="-- Select Voyage --"
                                    />
                                </div>

                                <div className="w-48">
                                    <SearchableSelect
                                        options={[
                                            {value: '', label: 'All Shippers'},
                                            ...shippers.map(shipper => ({
                                                value: shipper.id,
                                                label: `${shipper.code} - ${shipper.shortname || shipper.name}`
                                            }))
                                        ]}
                                        value={selectedShipperId}
                                        onChange={setSelectedShipperId}
                                        placeholder="All Shippers"
                                        disabled={!selectedVoyage || shippers.length === 0}
                                    />
                                </div>

                                <select
                                    className="w-40 px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    disabled={!selectedVoyage}
                                >
                                    <option value="">Sort by...</option>
                                    <option value="refNo">Ref No</option>
                                    <option value="dealer">Shipper</option>
                                    <option value="port">POD</option>
                                    <option value="orderNo">Booking Ref</option>
                                    <option value="consignee">Consignee</option>
                                    <option value="chassisNo">Chassis No</option>
                                    <option value="make/model">Make/Model</option>
                                </select>

                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={includeRelated}
                                        onChange={(e) => setIncludeRelated(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">Include Related</span>
                                </label>

                                {selectedVoyage && (
                                    <button
                                        onClick={generatePDF}
                                        disabled={generating}
                                        className="px-5 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
                                    >
                                        {generating ? 'Generating...' : 'Save as PDF'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Preview */}
                        {loading && (
                            <div
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                                <p className="text-gray-500 dark:text-gray-400">Loading cars...</p>
                            </div>
                        )}

                        {selectedVoyage && !loading && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                <div className="p-4 bg-gray-50 border-b border-gray-200">
                                    <span className="text-sm font-medium text-gray-600">Preview</span>
                                    <span className="text-sm text-gray-400 ml-2">({filteredCars.length} cars)</span>
                                </div>
                                <div className="p-4">
                                    <InventoryList
                                        voyageNo={selectedVoyage.voyage_code}
                                        date={formatDateOrToday(selectedVoyage.departure)}
                                        shipName={selectedVoyage.vessel_description}
                                        loadingPort={selectedVoyage.port_name || ''}
                                        shipperName={selectedShipper ? `${selectedShipper.code} - ${selectedShipper.name}` : undefined}
                                        cars={inventoryCars}
                                        includeRelated={includeRelated}
                                    />
                                </div>
                            </div>
                        )}

                        {!selectedVoyage && !loading && (
                            <div
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                                <div className="text-gray-400 dark:text-gray-500">
                                    <svg className="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24"
                                         stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                                    </svg>
                                    <p>Select a voyage to preview the parking list</p>
                                </div>
                            </div>
                        )}
                    </div>
                </Layout>
            </div>
        </>
    );
};

export default ParkingListPage;
